import { useState, useEffect, useMemo } from 'react';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;
const EMOJIS = { Bags:'🎒',Drinkware:'🥤',Apparel:'👕',Tech:'⚡',Office:'📓' };

// PSRESTful returns hex without '#' prefix
const hexBg = (hex, alpha = '', fallback = '#F8FAFC') => {
  if (!hex) return fallback;
  const clean = hex.startsWith('#') ? hex : `#${hex}`;
  return clean + alpha;
};

const cardImageUrl = (p) => {
  const imgs = p.images || [];
  return imgs.find(i => i.type === 'primary')?.url || imgs[0]?.url || null;
};

// Also strips HTML some suppliers send in descriptions (<ul><li>…). The sync
// now stores plain text, but rows synced before that fix still carry tags.
const cleanDescription = (d) =>
  (d || '')
    .replace(/<\s*li[^>]*>/gi, ' • ').replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&sup2;/gi, '\u00b2')
    .replace(/\s+\./g, '.').replace(/\s+,/g, ',').replace(/\s{2,}/g, ' ').trim();

const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { month:'long', day:'numeric', year:'numeric' });
  } catch { return dateStr; }
};

// Client-facing prices are computed on the server (GET /api/p/:token) and
// arrive precomputed; this page never sees supplier cost or margin. Normalize
// anyway: there is no error boundary, so .toFixed on a non-number blanks the page.
const num = (v) => {
  const n = Number(v);
  return v != null && v !== '' && Number.isFinite(n) ? n : null;
};

// ─── Loading / error / approved states ────────────────────────────────────────
function Loading() {
  return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F8FAFC' }}>
      <div style={{ fontSize:13,color:'#94A3B8' }}>Loading proposal...</div>
    </div>
  );
}

// The failure screen. Previously this ALWAYS said "invalid or has expired",
// for any failure at all — a 401, a 500, a rate-limit, a dropped connection.
// That message cost hours of debugging on a link that was perfectly valid
// (a middleware bug was returning 401 to logged-out visitors), and worse, it
// told the distributor's client that their quote link was dead when it wasn't.
// Only a genuine 404 means the proposal is missing; everything else is our
// problem, not the link's, and should say so.
function NotFound({ status }) {
  const missing = status === '404';
  return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F8FAFC',padding:24 }}>
      <div style={{ background:'#fff',borderRadius:12,border:'1px solid #E2E8F0',padding:40,maxWidth:420,textAlign:'center' }}>
        <div style={{ fontSize:48,marginBottom:12 }}>{missing ? '🤷' : '⚠️'}</div>
        <div style={{ fontSize:18,fontWeight:600,color:'#0F172A',marginBottom:8 }}>
          {missing ? 'Proposal not found' : 'Couldn\u2019t load this proposal'}
        </div>
        <div style={{ fontSize:13,color:'#64748B',lineHeight:1.6 }}>
          {missing
            ? 'This proposal link is invalid or has expired. Please contact the sender for a new link.'
            : 'Something went wrong on our end — the link itself is fine. Please refresh, or try again in a moment.'}
        </div>
        {!missing && (
          <>
            <button onClick={() => window.location.reload()}
              style={{ marginTop:18, background:'#0F172A', border:'none', borderRadius:8,
                padding:'9px 18px', color:'#fff', fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
              Try again
            </button>
            {status && (
              <div style={{ marginTop:12, fontSize:11, color:'#CBD5E1' }}>Reference: {status}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Inclusive apparel pricing for the client: a per-size grid with decoration
// folded into each unit price, setup fees itemized separately, then the line
// total. Keeps the client from seeing decoration as a confusing separate line.
function ApparelClientPricing({ rows, setups, total, shippingBlended }) {
  const th = { fontSize:10,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',padding:'6px 10px' };
  const td = { fontSize:12,color:'#0F172A',padding:'7px 10px' };
  return (
    <div>
      <table style={{ width:'100%',borderCollapse:'collapse' }}>
        <thead>
          <tr style={{ borderBottom:'1px solid #CBD5E1' }}>
            <th style={{ ...th, textAlign:'left' }}>Size</th>
            <th style={{ ...th, textAlign:'right' }}>Qty</th>
            <th style={{ ...th, textAlign:'right' }}>Price/pc</th>
            <th style={{ ...th, textAlign:'right' }}>Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r,i) => (
            <tr key={i} style={{ borderBottom: i < rows.length-1 ? '1px solid #F1F5F9' : 'none' }}>
              <td style={{ ...td, fontWeight:600 }}>{r.label}</td>
              <td style={{ ...td, textAlign:'right' }}>{r.qty}</td>
              <td style={{ ...td, textAlign:'right',color:G,fontWeight:600 }}>${r.unit.toFixed(2)}</td>
              <td style={{ ...td, textAlign:'right' }}>${r.subtotal.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {setups.length > 0 && (
        <div style={{ marginTop:10,paddingTop:10,borderTop:'1px dashed #E2E8F0' }}>
          <div style={{ fontSize:10,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6 }}>Setup</div>
          {setups.map((c,i) => (
            <div key={i} style={{ display:'flex',justifyContent:'space-between',fontSize:12,color:'#475569',padding:'2px 0' }}>
              <span>{c.label}</span>
              <span style={{ fontWeight:500 }}>${(num(c.amount) ?? 0).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display:'flex',justifyContent:'space-between',marginTop:10,paddingTop:10,borderTop:'1px solid #E2E8F0' }}>
        <span style={{ fontSize:12,fontWeight:600,color:'#0F172A' }}>Line total</span>
        <span style={{ fontSize:14,fontWeight:600,color:'#0F172A' }}>${total.toFixed(2)}</span>
      </div>
      {shippingBlended && (
        <div style={{ marginTop:8,fontSize:11,color:'#64748B',fontStyle:'italic',lineHeight:1.5 }}>
          * Shipping is included in the prices shown above.
        </div>
      )}
    </div>
  );
}

// ─── Product card (read-only client view) ────────────────────────────────────
function ProductCard({ item, showPricing }) {
  const [imgError, setImgError] = useState(false);
  const p = item.product || item;
  const imgUrl = item.mockup_image || cardImageUrl(p);
  const description = useMemo(() => cleanDescription(p.description), [p.description]);
  const requestedColor = p.client_color || item.client_color;
  const showAllColors = !!item.show_all_colors;
  const showPriceGrid = !!item.show_price_grid;
  const allColors = (p.colors || []).slice(0, 24);
  // Every price below is computed on the server by the shared pricing module
  // (server/lib/presentationPricing.js) — the same one the quote PDF and the
  // invoice use — and arrives in item.pricing. This page does no pricing math,
  // it only displays. item.pricing is absent when pricing is hidden.
  const pr = showPricing && item.pricing && typeof item.pricing === 'object' ? item.pricing : null;
  const qty = num(item.qty) || num(p.min_qty) || 1;
  // Unit price already includes blended shipping when the rep chose to blend it.
  const sellPrice = pr ? num(pr.unit_price) : null;
  const lineSubtotal = pr ? (num(pr.merchandise_total) ?? 0) : 0;
  const lineTotal = pr ? (num(pr.line_total) ?? 0) : 0;
  const charges = pr && Array.isArray(pr.charges) ? pr.charges : [];
  const shippingBlended = !!(pr && pr.shipping_blended);

  // Apparel client view: an inclusive per-size grid (size price + any blended
  // decoration/setup + per-piece decoration + blended shipping in each size's
  // price). Setup fees that weren't blended still list below.
  const apparelRows = (pr && item.apparel && Array.isArray(pr.sizes) ? pr.sizes : [])
    .map(r => ({ label: String(r.label ?? ''), qty: num(r.qty) ?? 0, unit: num(r.unit_price) ?? 0, subtotal: num(r.amount) ?? 0 }));
  const useApparelView = apparelRows.length > 0;
  const setupCharges = useApparelView ? charges : [];

  // Volume grid rows (tier overrides and blended shipping already applied; the
  // row for the ordered quantity is exactly the price that gets billed).
  const tierPrices = showPriceGrid && pr && Array.isArray(pr.tiers)
    ? pr.tiers.map(t => ({ qty: t.qty, sell: num(t.unit_price) ?? 0 }))
    : [];

  return (
    <div style={{ background:'#fff',borderRadius:12,border:'1px solid #E2E8F0',overflow:'hidden',marginBottom:16 }}>
      <div style={{ display:'grid',gridTemplateColumns:'180px 1fr',gap:0 }}>
        {/* Product image */}
        <div style={{ background:'#F8FAFC',display:'flex',alignItems:'center',justifyContent:'center',
          minHeight:180,fontSize:60,overflow:'hidden',borderRight:'1px solid #E2E8F0' }}>
          {imgUrl && !imgError ? (
            <img src={imgUrl} alt={p.name} onError={()=>setImgError(true)}
              style={{ maxWidth:'100%',maxHeight:180,objectFit:'contain',padding:8 }}/>
          ) : (
            <span>{EMOJIS[p.category]||'📦'}</span>
          )}
        </div>
        {/* Details */}
        <div style={{ padding:'18px 20px',display:'flex',flexDirection:'column',justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:15,fontWeight:600,color:'#0F172A',marginBottom:6 }}>{p.name}</div>
            {description && (
              <div style={{ fontSize:12,color:'#64748B',lineHeight:1.55,marginBottom:10 }}>{description}</div>
            )}
            <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginBottom:10 }}>
              <span style={{ background:'#F1F5F9',color:'#475569',fontSize:11,padding:'3px 9px',borderRadius:6 }}>
                Quantity: {qty}
              </span>
              {!showAllColors && requestedColor && (
                <span style={{ background:`${G}18`,color:'#059669',fontSize:11,padding:'3px 9px',borderRadius:6,
                  display:'inline-flex',alignItems:'center',gap:5 }}>
                  <span style={{ width:9,height:9,borderRadius:'50%',
                    background:hexBg(requestedColor.hex,'','#ccc'),border:'1px solid rgba(0,0,0,0.1)' }}/>
                  {requestedColor.name || requestedColor}
                </span>
              )}
              {(p.decoration_methods||[]).slice(0,3).map((d,i)=>(
                <span key={i} style={{ background:'#F1F5F9',color:'#475569',fontSize:11,padding:'3px 9px',borderRadius:6 }}>{d}</span>
              ))}
            </div>
            {showAllColors && allColors.length > 0 && (
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6 }}>
                  Available in {allColors.length} color{allColors.length===1?'':'s'}
                </div>
                <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
                  {allColors.map((c,i) => (
                    <div key={i} style={{ display:'inline-flex',alignItems:'center',gap:5,
                      background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:14,padding:'2px 10px 2px 4px' }}>
                      <span style={{ width:14,height:14,borderRadius:'50%',
                        background:hexBg(c.hex,'','#ccc'),border:'1px solid rgba(0,0,0,0.1)' }}/>
                      <span style={{ fontSize:11,color:'#475569' }}>{c.client_label || c.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {item.note && (
              <div style={{ fontSize:12,color:'#475569',background:`${G}0D`,
                borderLeft:`2px solid ${G}`,borderRadius:'0 6px 6px 0',
                padding:'8px 12px',marginBottom:10,fontStyle:'italic',lineHeight:1.5 }}>
                {item.note}
              </div>
            )}
          </div>
          {pr && (useApparelView || sellPrice != null) && (
            <div style={{ marginTop:8,paddingTop:12,borderTop:'1px solid #F1F5F9' }}>
              {useApparelView ? (
                <ApparelClientPricing rows={apparelRows} setups={setupCharges} total={lineTotal} shippingBlended={shippingBlended}/>
              ) : (
              <>
              {showPriceGrid && tierPrices.length > 0 ? (
                <div>
                  <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:8 }}>
                    Volume pricing
                  </div>
                  <table style={{ width:'100%',borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom:'1px solid #CBD5E1' }}>
                        <th style={{ textAlign:'left',padding:'6px 10px',fontSize:10,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px' }}>
                          Quantity
                        </th>
                        <th style={{ textAlign:'right',padding:'6px 10px',fontSize:10,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px' }}>
                          Price each
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tierPrices.map((t,i) => (
                        <tr key={i} style={{ borderBottom: i < tierPrices.length-1 ? '1px solid #F1F5F9' : 'none' }}>
                          <td style={{ padding:'7px 10px',fontSize:12,color:'#0F172A' }}>{t.qty}+ pieces</td>
                          <td style={{ padding:'7px 10px',fontSize:13,fontWeight:600,color:G,textAlign:'right' }}>${t.sell.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Shipping-included footnote — only when this item is in
                      blended mode. Clarifies that the tier prices already
                      include shipping so the client doesn't expect a separate
                      shipping line. */}
                  {shippingBlended && (
                    <div style={{ marginTop:8,fontSize:11,color:'#64748B',fontStyle:'italic',lineHeight:1.5 }}>
                      * Shipping is included in the prices shown above.
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-end' }}>
                    <div>
                      <div style={{ fontSize:11,color:'#94A3B8' }}>Unit price</div>
                      <div style={{ fontSize:18,fontWeight:600,color:G }}>${sellPrice.toFixed(2)}</div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:11,color:'#94A3B8' }}>Item subtotal ({qty})</div>
                      <div style={{ fontSize:14,fontWeight:500,color:'#475569' }}>${lineSubtotal.toFixed(2)}</div>
                    </div>
                  </div>
                  {/* Same footnote in single-price mode. */}
                  {shippingBlended && (
                    <div style={{ marginTop:8,fontSize:11,color:'#64748B',fontStyle:'italic',lineHeight:1.5 }}>
                      * Shipping is included in the unit price shown above.
                    </div>
                  )}
                </>
              )}
              {charges.length > 0 && (
                <div style={{ marginTop:10,paddingTop:10,borderTop:'1px dashed #E2E8F0' }}>
                  <div style={{ fontSize:10,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6 }}>
                    Decoration & Setup
                  </div>
                  {charges.map((c, i) => {
                    const amt = num(c.amount) ?? 0;
                    return (
                      <div key={i} style={{ display:'flex',justifyContent:'space-between',fontSize:12,color:'#475569',padding:'2px 0' }}>
                        <span>{c.label}{c.type==='Run' && c.qty ? ` (×${c.qty})` : ''}</span>
                        <span style={{ fontWeight:500 }}>${amt.toFixed(2)}</span>
                      </div>
                    );
                  })}
                  {!showPriceGrid && (
                    <div style={{ display:'flex',justifyContent:'space-between',marginTop:6,paddingTop:6,borderTop:'1px solid #F8FAFC' }}>
                      <span style={{ fontSize:12,fontWeight:600,color:'#0F172A' }}>Line total</span>
                      <span style={{ fontSize:14,fontWeight:600,color:'#0F172A' }}>${lineTotal.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}
              </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Request changes modal ───────────────────────────────────────────────────
function ChangesModal({ token, onClose, onSuccess }) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!message.trim()) { setError('Please describe what you would like changed'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`/api/p/${token}/changes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      if (!r.ok) throw new Error('Server error');
      onSuccess();
    } catch (e) {
      setError('Could not submit. Please try again or contact us directly.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:100,
      display:'flex',alignItems:'center',justifyContent:'center',padding:24,backdropFilter:'blur(4px)' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:480,overflow:'hidden' }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:'1px solid #E2E8F0' }}>
          <div>
            <div style={{ fontSize:16,fontWeight:600,color:'#0F172A' }}>Request changes</div>
            <div style={{ fontSize:12,color:'#94A3B8',marginTop:2 }}>Tell us what you'd like to adjust</div>
          </div>
          <button onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',fontSize:22,color:'#94A3B8',padding:'0 4px',fontFamily:'inherit' }}>×</button>
        </div>
        <div style={{ padding:24 }}>
          <textarea value={message} onChange={e=>setMessage(e.target.value)}
            placeholder="e.g., Could we see this in navy instead? Or swap the bottle for a tumbler?"
            rows={6}
            style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 12px',fontSize:13,
              fontFamily:'inherit',outline:'none',color:'#0F172A',resize:'vertical',boxSizing:'border-box' }}/>
          {error && <div style={{ fontSize:12,color:'#EF4444',marginTop:8 }}>{error}</div>}
        </div>
        <div style={{ padding:'14px 24px',borderTop:'1px solid #E2E8F0',display:'flex',gap:10,justifyContent:'flex-end' }}>
          <button onClick={onClose} disabled={submitting}
            style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 18px',fontSize:13,cursor:'pointer',fontFamily:'inherit',color:'#64748B' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={submitting}
            style={{ background:G,border:'none',borderRadius:8,padding:'10px 22px',color:'#fff',fontSize:13,fontWeight:600,
              cursor:submitting?'wait':'pointer',fontFamily:'inherit',opacity:submitting?0.7:1 }}>
            {submitting ? 'Sending...' : 'Send Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main public presentation viewer ─────────────────────────────────────────
export default function PublicPresentation({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showChanges, setShowChanges] = useState(false);
  const [approving, setApproving] = useState(false);
  // Local override that flips to 'approved'/'changes_requested' immediately
  // after the user takes action, without needing a re-fetch.
  const [statusOverride, setStatusOverride] = useState(null);

  useEffect(() => {
    fetch(`/api/p/${token}`)
      .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);
  const approve = async () => {
    if (!window.confirm('Approve this proposal? This signals to the team to proceed with your order.')) return;
    setApproving(true);
    try {
      const r = await fetch(`/api/p/${token}/approve`, { method: 'POST' });
      if (!r.ok) throw new Error();
      setStatusOverride('approved');
    } catch {
      alert('Could not record approval. Please try again or contact us directly.');
    } finally {
      setApproving(false);
    }
  };

  if (loading) return <Loading />;
  if (error || !data) return <NotFound status={error} />;

  const status = statusOverride || data.status || 'draft';
  const products = Array.isArray(data.products) ? data.products : [];
  const showPricing = !!data.show_pricing;
  const additionalCharges = showPricing && Array.isArray(data.additional_charges) ? data.additional_charges : [];

  // Totals come precomputed from the server's shared pricing module, so the
  // page, the PDF and the invoice always agree. Line-mode shipping arrives as
  // its own lines; blended shipping is already inside each unit price.
  const lineShipping = showPricing && Array.isArray(data.shipping_lines) ? data.shipping_lines : [];
  const grandTotal = showPricing ? (num(data.total) ?? 0) : 0;

  return (
    <div style={{ minHeight:'100vh',background:'#F8FAFC',fontFamily:"'DM Sans',system-ui,sans-serif",paddingBottom:48 }}>
      {/* Header */}
      <header style={{ background:BRAND.colors.dark,color:'#fff' }}>
        <div style={{ maxWidth:900,margin:'0 auto',padding:'28px 24px',display:'flex',
          alignItems:'flex-end',justifyContent:'space-between',gap:16,flexWrap:'wrap' }}>
          <div style={{ display:'flex',alignItems:'center',gap:14 }}>
            {data.logo_url ? (
              <img src={data.logo_url} alt={data.company}
                style={{ width:48,height:48,borderRadius:8,objectFit:'contain',background:'rgba(255,255,255,0.06)',padding:6 }}/>
            ) : (
              <div style={{ width:48,height:48,borderRadius:8,background:G,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:20,fontWeight:700 }}>
                {(data.company||'?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <div style={{ fontSize:20,fontWeight:700,letterSpacing:'-0.3px' }}>{data.company}</div>
              {data.tagline && (
                <div style={{ fontSize:11,color:G,marginTop:2,fontWeight:500,letterSpacing:0.8,textTransform:'uppercase' }}>
                  {data.tagline}
                </div>
              )}
            </div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:11,color:'#94A3B8' }}>Prepared for</div>
            <div style={{ fontSize:18,fontWeight:500 }}>{data.client_name || 'Your Client'}</div>
            <div style={{ fontSize:11,color:'#94A3B8',marginTop:3 }}>
              {fmtDate(data.created_at) || fmtDate(new Date().toISOString())}
            </div>
          </div>
        </div>
        <div style={{ height:3,background:G }}/>
      </header>

      {/* Status banner if approved or changes requested */}
      {status === 'approved' && (
        <div style={{ background:`${G}18`,borderBottom:`1px solid ${G}33`,padding:'12px 24px',textAlign:'center',color:'#059669',fontSize:13,fontWeight:500 }}>
          ✓ Approved — {data.company} has been notified and will be in touch shortly.
        </div>
      )}
      {status === 'changes_requested' && (
        <div style={{ background:'#FEF3C7',borderBottom:'1px solid #FCD34D',padding:'12px 24px',textAlign:'center',color:'#92400E',fontSize:13 }}>
          Changes requested — {data.company} will follow up with a revised proposal.
        </div>
      )}

      {/* Body */}
      <main style={{ maxWidth:900,margin:'0 auto',padding:'28px 24px' }}>
        {/* Title row */}
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:20,
          paddingBottom:14,borderBottom:'1px solid #E2E8F0',flexWrap:'wrap',gap:8 }}>
          <div style={{ fontSize:18,fontWeight:600,color:'#0F172A' }}>
            {data.name || 'Your proposal'}
          </div>
          <div style={{ fontSize:12,color:'#94A3B8' }}>
            {products.length} {products.length === 1 ? 'item' : 'items'}
          </div>
        </div>

        {/* Product cards */}
        {products.length === 0 ? (
          <div style={{ textAlign:'center',padding:'48px 24px',color:'#94A3B8',background:'#fff',borderRadius:12,border:'1px solid #E2E8F0' }}>
            This proposal has no items yet.
          </div>
        ) : (
          products.map((item, i) => (
            <ProductCard key={i} item={item} showPricing={showPricing}/>
          ))
        )}

        {/* Additional charges (presentation-level) */}
        {showPricing && additionalCharges.length > 0 && (
          <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:'16px 20px',marginTop:8,marginBottom:8 }}>
            <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:10 }}>
              Additional Charges
            </div>
            {additionalCharges.map((c, i) => (
              <div key={i} style={{ display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:13,color:'#475569' }}>
                <span>{c.label || 'Charge'}</span>
                <span style={{ fontWeight:500,color:'#0F172A' }}>${(num(c.amount) ?? 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Shipping lines — one per item that has shipping_mode='line'.
            Blended shipping is already baked into each item's displayed prices
            (in ProductCard) and doesn't appear here. */}
        {showPricing && lineShipping.length > 0 && (
          <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:'14px 20px',marginTop:8,marginBottom:8 }}>
            {lineShipping.map((s, i) => (
              <div key={i} style={{ display:'flex',justifyContent:'space-between',fontSize:13,color:'#475569',padding:'4px 0' }}>
                <span>{s.label}</span>
                <span style={{ fontWeight:500,color:'#0F172A' }}>${(num(s.amount) ?? 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Total — hidden if ANY item shows a volume pricing grid. The grid
            invites the client to pick any tier; a fixed "Estimated total" tied
            to the distributor's preset qty contradicts that. Show one or the
            other, not both. */}
        {showPricing && products.length > 0 && !products.some(it => it.show_price_grid) && (
          <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:'18px 22px',marginTop:8 }}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'baseline' }}>
              <div style={{ fontSize:14,fontWeight:600,color:'#0F172A' }}>Estimated total</div>
              <div style={{ fontSize:22,fontWeight:700,color:G }}>${grandTotal.toFixed(2)}</div>
            </div>
            <div style={{ fontSize:11,color:'#94A3B8',marginTop:6,lineHeight:1.5 }}>
              Final pricing may vary based on shipping and applicable taxes.
            </div>
          </div>
        )}

        {/* Action buttons */}
        {status === 'draft' && (
          <div style={{ background:'#fff',border:`1px solid ${G}33`,borderRadius:12,padding:'24px',marginTop:20,textAlign:'center' }}>
            <div style={{ fontSize:15,fontWeight:600,color:'#0F172A',marginBottom:6 }}>Ready to move forward?</div>
            <div style={{ fontSize:13,color:'#64748B',marginBottom:18 }}>
              Approve to proceed, or request changes if you'd like to adjust anything.
            </div>
            <div style={{ display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap' }}>
              <button onClick={approve} disabled={approving}
                style={{ background:G,border:'none',borderRadius:10,padding:'12px 28px',color:'#fff',
                  fontSize:14,fontWeight:600,cursor:approving?'wait':'pointer',fontFamily:'inherit',opacity:approving?0.7:1 }}>
                {approving ? 'Approving...' : '✓ Approve this Proposal'}
              </button>
              <button onClick={()=>setShowChanges(true)} disabled={approving}
                style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:10,padding:'12px 22px',
                  color:'#475569',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
                Request changes
              </button>
            </div>
            <div style={{ fontSize:11,color:'#94A3B8',marginTop:14 }}>
              Questions? Contact {data.contact_email || data.company} directly.
            </div>
          </div>
        )}

        {/* Contact card (always visible at the bottom) */}
        {(data.contact_phone || data.contact_email) && (
          <div style={{ display:'flex',justifyContent:'center',gap:24,marginTop:24,
            fontSize:12,color:'#64748B',flexWrap:'wrap' }}>
            {data.contact_phone && <span>📞 {data.contact_phone}</span>}
            {data.contact_email && (
              <a href={`mailto:${data.contact_email}`} style={{ color:G,textDecoration:'none' }}>
                ✉ {data.contact_email}
              </a>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{ marginTop:48,padding:'18px 24px',textAlign:'center',fontSize:11,color:'#94A3B8' }}>
        Powered by {BRAND.name}
      </footer>

      {/* Modals */}
      {showChanges && (
        <ChangesModal token={token}
          onClose={()=>setShowChanges(false)}
          onSuccess={()=>{ setShowChanges(false); setStatusOverride('changes_requested'); }}/>
      )}
    </div>
  );
}
